
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, Settings } from "lucide-react";
import { Link } from "react-router-dom";

interface Kit {
  id: string;
  name: string;
  number: number;
}

interface KitSelectorProps {
  activeKit: string | null;
  onKitSelect: (kitId: string) => void;
  onApplyChanges: () => void;
  hasUnsavedChanges: boolean;
}

export const KitSelector: React.FC<KitSelectorProps> = ({
  activeKit,
  onKitSelect,
  onApplyChanges,
  hasUnsavedChanges,
}) => {
  const { data: kits = [], isLoading } = useQuery({
    queryKey: ['kits'],
    queryFn: async () => {
      console.log('Fetching kits...');
      const { data, error } = await supabase
        .from('kits')
        .select('id, name, number')
        .order('number');
      
      if (error) {
        console.error('Error fetching kits:', error);
        throw error;
      }
      console.log('Fetched kits:', data);
      return data as Kit[];
    },
    retry: 1,
    staleTime: 30000,
  });

  return (
    <Card className="p-4 bg-ticker-bg text-ticker-text">
      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <Select
            value={activeKit || undefined}
            onValueChange={onKitSelect}
            disabled={isLoading}
          >
            <SelectTrigger className="w-full bg-ticker-muted/10">
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading kits...</span>
                </div>
              ) : (
                <SelectValue placeholder="Select a kit" />
              )}
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-200 shadow-lg">
              {kits.length === 0 ? (
                <SelectItem value="no-kits" disabled>
                  No kits available
                </SelectItem>
              ) : (
                kits.map((kit) => (
                  <SelectItem 
                    key={kit.id} 
                    value={kit.id}
                    className="cursor-pointer"
                  >
                    Kit {kit.number}: {kit.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={onApplyChanges}
          disabled={!hasUnsavedChanges}
          className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50"
        >
          Apply Changes
        </Button>
        <Link to="/settings">
          <Button 
            variant="outline" 
            size="icon"
            className="bg-ticker-muted/10 hover:bg-ticker-muted/20 text-ticker-text border-ticker-muted/20"
          >
            <Settings className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </Card>
  );
};
